// ── Fee LINES — the named fee heads a student owes, finer than the 6 categories ──
//
// Defect 7 (QA, 2026-10-05): "IIT Fee is included in Tuition fee instead of being
// displayed separately". Root cause, exactly: `rowHeadComposition`
// (./head-breakdown.ts) buckets every priced plan component into one of the six
// ACCOUNTING heads (`FEE_HEADS`) by NAME — `classifyFeeHead` in the API matches
// "tuition", "admission", boarding words and "transport"/"bus"/"van" — and a name
// it cannot place ("IIT Fee", "Books", "Lab Fee", "Annual Day") falls back to
// TUITION by an owner decision (a plan line that names nothing is "the class fee").
// So the Collect drawer's "Apply towards" chips, the By-head table, the parent's
// "why" sheet and the statement PDF all folded IIT into Tuition. Nothing is stored
// wrong: `FeePayment.feeHead` / `appliedHead` stay the accounting category, the
// ledger books by category and no amount moved. The merge is purely in what these
// readers could name.
//
// The six categories are a deliberate accounting vocabulary (ledger income
// accounts, concession scopes, late-fee rules, head reports key on them), so they
// stay. What this module adds is one level BELOW them: a LINE per distinct plan
// component the category fallback used to swallow.
//
//   · a component the classifier DOES place ("Tuition Fee", "Admission Fee",
//     "Hostel Mess") stays on its category line, labelled by the category — exactly
//     what every reader showed before;
//   · a component it cannot place gets its OWN line, keyed by its normalised name
//     and labelled by its real name ("IIT Fee"). Its `head` is still TUITION, so
//     the books, concessions and head reports are unchanged.
//
// Lines are split out only where a row is genuinely a bundle of several priced
// components (rule (a) of `rowHeadComposition`). A row backed by one component, a
// stamped single-head row or a bundle of unknown composition keeps its category
// line — nothing is guessed.
//
// Allocation is recorded on the collection event: `FeePaymentCollection.appliedHead`
// is the category (as always) and the new `appliedComponent` is the line's TOKEN —
// "c:IIT Fee" for a custom line, or the category itself ("TUITION") for the
// category's own line when a sibling custom line shares it. NULL means the event
// named only a category (every historical row, every gateway/manual/enrolment
// event): the line engine spreads it across that category's lines by remaining
// capacity, the same rule the head engine applies to undirected money.
//
// Pure: no Prisma, no Date, no I/O. For a row with no custom lines this engine
// produces the SAME figures as the head engine in ./head-breakdown.ts (pinned by
// head-lines.test.ts across a grid of inputs), so category totals and line totals
// can never tell two stories. Both engines take the same optional `pinned` waivers
// (CAMP-2026-0942: a refunded line's cancelled charge belongs to ITS line); with none
// the figures are byte-identical to what they were before the input existed.

import { FEE_HEADS, FEE_HEAD_LABELS, type FeeHead } from "./fee-heads.js";
import {
  HEAD_MONEY_EPSILON,
  rowHeadComposition,
  rupeesToPaiseNumber,
  type PinnedWaiver,
  type RowCompositionInput,
  type RowMoney,
  type SeveralLine,
} from "./head-breakdown.js";

/** A custom (name-keyed) component token starts with this; a category's own token is the bare `FeeHead`. */
export const COMPONENT_TOKEN_PREFIX = "c:";

/** Longest component token the API accepts (the plan editor's own name limit is far below this). */
export const COMPONENT_TOKEN_MAX = 120;

const collapse = (name: string): string => name.replace(/\s+/g, " ").trim();

/** The matching form of a component name: whitespace collapsed, trimmed, lower-cased. */
export function normalizeComponentName(name: string): string {
  return collapse(name).toLowerCase();
}

/** The token a client sends (and the server stores) for the custom line named `name`. */
export function componentToken(name: string): string {
  return `${COMPONENT_TOKEN_PREFIX}${collapse(name)}`.slice(0, COMPONENT_TOKEN_MAX);
}

/**
 * The line key a stored/sent token points at: the `FeeHead` itself for a category
 * line, `c:<normalised name>` for a custom one, `null` for anything else (empty,
 * unknown, a bare `c:`).
 */
export function lineKeyOfToken(token: string | null | undefined): string | null {
  const t = (token ?? "").trim();
  if (!t) return null;
  if ((FEE_HEADS as readonly string[]).includes(t)) return t;
  if (t.startsWith(COMPONENT_TOKEN_PREFIX)) {
    const name = normalizeComponentName(t.slice(COMPONENT_TOKEN_PREFIX.length));
    return name ? `${COMPONENT_TOKEN_PREFIX}${name}` : null;
  }
  return null;
}

/**
 * The form a token is STORED in: a category token as is, a custom one with its name's
 * whitespace collapsed (so "c:  IIT   Fee " is written "c:IIT Fee"). `null` for anything
 * `lineKeyOfToken` cannot resolve.
 */
export function canonicalComponentToken(token: string | null | undefined): string | null {
  const key = lineKeyOfToken(token);
  if (key === null) return null;
  if (!key.startsWith(COMPONENT_TOKEN_PREFIX)) return key;
  return componentToken((token ?? "").trim().slice(COMPONENT_TOKEN_PREFIX.length));
}

/** What a person reads for a stored token: "IIT Fee" for "c:IIT Fee", "Tuition" for "TUITION". Empty for an unresolvable token. */
export function labelOfComponentToken(token: string | null | undefined): string {
  const key = lineKeyOfToken(token);
  if (key === null) return "";
  if (key.startsWith(COMPONENT_TOKEN_PREFIX)) return collapse((token ?? "").trim().slice(COMPONENT_TOKEN_PREFIX.length));
  return FEE_HEAD_LABELS[key as FeeHead];
}

/** True when `token` is something `lineKeyOfToken` can resolve. */
export function isComponentToken(token: unknown): token is string {
  return typeof token === "string" && token.length <= COMPONENT_TOKEN_MAX && lineKeyOfToken(token) !== null;
}

// ── Row composition, line level ──────────────────────────────────────────────

export interface LineSpec {
  /** `FeeHead` for the category's own line, `c:<normalised name>` for a custom one. */
  key: string;
  /** The ACCOUNTING category this line books to (TUITION for "IIT Fee"). */
  head: FeeHead;
  /** What a person reads: the category label, or the component's own name. */
  label: string;
  custom: boolean;
  /** What a client sends back as `applyTo.component`. */
  token: string;
  weight: number;
}

export type RowLineComposition = { kind: "lines"; lines: LineSpec[] } | { kind: "bundle" };

const HEAD_ORDER = FEE_HEADS as readonly FeeHead[];

function categoryLine(head: FeeHead, weight: number): LineSpec {
  return { key: head, head, label: FEE_HEAD_LABELS[head], custom: false, token: head, weight };
}

/** Canonical order: category order, a category's own line first, then its custom lines A to Z. */
function compareLines(a: Pick<LineSpec, "head" | "custom" | "label" | "key">, b: Pick<LineSpec, "head" | "custom" | "label" | "key">): number {
  return (
    HEAD_ORDER.indexOf(a.head) - HEAD_ORDER.indexOf(b.head) ||
    Number(a.custom) - Number(b.custom) ||
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()) ||
    (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
  );
}

/**
 * Which LINES a row's face value belongs to. Mirrors `rowHeadComposition` exactly
 * (same row facts, same classifier, same rules for when a row is a bundle); the
 * only difference is that, in the one case where that function folds several plan
 * components onto categories (rule (a): a plan with MORE THAN ONE priced
 * component), a component the classifier cannot place keeps its own name instead
 * of becoming Tuition. Every other shape comes back as category lines, so the
 * head view and the line view agree wherever there is nothing to separate.
 */
export function rowLineComposition(
  row: RowCompositionInput,
  classify: (name: string) => FeeHead | null,
): RowLineComposition {
  const base = rowHeadComposition(row, classify);
  if (base.kind === "bundle") return { kind: "bundle" };
  if (base.kind === "single") return { kind: "lines", lines: [categoryLine(base.head, 1)] };

  const usable = (row.planComponents ?? []).filter((c) => Number.isFinite(c.amount) && c.amount > 0);
  const total = usable.reduce((s, c) => s + c.amount, 0);
  const splitsComponents = !!row.feePlanId && usable.length > 1 && total > HEAD_MONEY_EPSILON;
  if (!splitsComponents) {
    return { kind: "lines", lines: base.shares.map((s) => categoryLine(s.head, s.weight)) };
  }

  const byKey = new Map<string, LineSpec>();
  for (const c of usable) {
    const classified = classify(c.name);
    const spec: LineSpec = classified
      ? categoryLine(classified, c.amount / total)
      : (() => {
          const display = collapse(c.name);
          const key = lineKeyOfToken(componentToken(display));
          // A name that normalises to nothing cannot reach here (the API drops nameless
          // components), but a total function beats a thrown error in a read path.
          if (!key) return categoryLine("TUITION", c.amount / total);
          return { key, head: "TUITION" as FeeHead, label: display, custom: true, token: componentToken(display), weight: c.amount / total };
        })();
    const seen = byKey.get(spec.key);
    if (seen) seen.weight += spec.weight;
    else byKey.set(spec.key, spec);
  }
  return { kind: "lines", lines: [...byKey.values()].sort(compareLines) };
}

// ── Per-row line breakdown ───────────────────────────────────────────────────

/** Σ of collection events that named `head`, and (optionally) a line token. */
export interface LineDirected {
  head: FeeHead;
  /** The event's `appliedComponent`; null = it named only the category. */
  token: string | null;
  amount: number;
}

export interface LineHeadLine {
  key: string;
  head: FeeHead;
  label: string;
  custom: boolean;
  token: string;
  billed: number;
  concession: number;
  /**
   * CAMP-2026-1019 - the part of `concession` that is a fee-head REFUND's cancelled charge (a pinned waiver), not a
   * concession anybody was granted. `concession` keeps carrying it so billed - concession + fine = net still holds;
   * a screen that labels the figure "Concession" shows `concession - refunded` and the refund as its own number.
   * Omitted (0) for a line nobody refunded.
   */
  refunded?: number;
  fine: number;
  /** billed − concession + fine, clamped at 0. */
  net: number;
  paid: number;
  /** max(0, net − paid). Never negative. */
  due: number;
}

export interface RowLineBreakdown {
  lines: LineHeadLine[];
  several: SeveralLine | null;
  advance: number;
}

const clamp0 = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);
const toP = (rupees: number): number => rupeesToPaiseNumber(clamp0(rupees));
const fromP = (paise: number): number => paise / 100;

/**
 * Split `totalP` integer paise across `weights` by the largest-remainder method:
 * every share is a whole number of paise, the shares sum to `totalP` EXACTLY, and
 * each is within one paisa of its proportional figure. (Rounding each share and
 * letting the last absorb the difference — what the float head engine does — can
 * leave the rounded parts a paisa off the whole; money cannot.) A tie on the
 * remainder goes to the later line, the one that has always absorbed the leftover.
 * No weight to speak of puts everything on the last line.
 */
function allocatePaise(totalP: number, weights: readonly number[]): number[] {
  const n = weights.length;
  const out = new Array<number>(n).fill(0);
  if (n === 0 || totalP <= 0) return out;
  const sum = weights.reduce((s, w) => s + clamp0(w), 0);
  if (sum <= 0) {
    out[n - 1] = totalP;
    return out;
  }
  const raw = weights.map((w) => (totalP * clamp0(w)) / sum);
  const floors = raw.map((r) => Math.floor(r + 1e-9));
  let left = totalP - floors.reduce((s, f) => s + f, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - (floors[i] ?? 0) }))
    .sort((a, b) => b.frac - a.frac || b.i - a.i);
  for (let k = 0; left > 0 && order.length > 0; k = (k + 1) % order.length) {
    const slot = order[k]!;
    floors[slot.i] = (floors[slot.i] ?? 0) + 1;
    left -= 1;
  }
  // Float noise can overshoot by a paisa: take it back from the largest share.
  while (left < 0) {
    let big = 0;
    floors.forEach((f, i) => { if (f > (floors[big] ?? 0)) big = i; });
    floors[big] = (floors[big] ?? 0) - 1;
    left += 1;
  }
  return floors;
}

/**
 * Split one row across its lines — the line-level twin of `computeRowHeadBreakdown`,
 * in INTEGER PAISE end to end so every figure is exact. Same rules step for step:
 * concession and fine by billed weight; directed money first; the undirected rest by
 * each line's remaining capacity; a correction that pulled more off the row than was
 * ever directed shrinks the directed lines pro rata. Invariants it keeps, to the paisa:
 *   1. Σ line billed == the row's billed; Σ line concession == its concession; Σ line
 *      fine == its fine;
 *   2. Σ line due == max(0, rowNet − rowPaid) for a non-bundle row with no advance;
 *   3. Σ line paid + advance == rowPaid;
 *   4. no due is negative; a concession never exceeds its line's billed.
 *
 * A directed event is attributed to its own line when it names one that THIS row
 * has (and the event's category agrees); otherwise it is category-level and is spread
 * over that category's lines by remaining capacity. An event for a category the row
 * does not touch is ignored, exactly as the head engine ignores it.
 */
export function computeRowLineBreakdown(
  money: RowMoney,
  composition: RowLineComposition,
  directed: readonly LineDirected[] = [],
  /** Waivers that belong to one line (a refunded fee line); see `PinnedWaiver`. Omitted = the concession is spread by weight, as ever. */
  pinned: readonly PinnedWaiver[] = [],
): RowLineBreakdown {
  const billedP = toP(money.billed);
  const concessionP = Math.min(toP(money.concession), billedP);
  const fineP = toP(money.fine);
  const rowNetP = Math.max(0, billedP - concessionP + fineP);
  const rowPaidP = toP(money.paid);

  if (composition.kind === "bundle") {
    const paidAppliedP = Math.min(rowPaidP, rowNetP);
    return {
      lines: [],
      several: {
        billed: fromP(billedP),
        concession: fromP(concessionP),
        fine: fromP(fineP),
        net: fromP(rowNetP),
        paid: fromP(paidAppliedP),
        due: fromP(Math.max(0, rowNetP - paidAppliedP)),
      },
      advance: fromP(Math.max(0, rowPaidP - rowNetP)),
    };
  }

  // Merge duplicate keys (defensive: the composition builder already does) and order.
  const merged = new Map<string, LineSpec>();
  for (const l of composition.lines) {
    const seen = merged.get(l.key);
    if (seen) seen.weight += l.weight;
    else merged.set(l.key, { ...l });
  }
  const specs = [...merged.values()].sort(compareLines);
  const n = specs.length;
  const weights = specs.map((l) => l.weight);

  const billedBy = allocatePaise(billedP, weights);
  // Pinned waivers first (a refunded line's cancelled charge belongs to THAT line), the rest by weight.
  const pinnedBy = new Array<number>(n).fill(0);
  let pinnedP = 0;
  for (const w of pinned) {
    const amountP = Math.min(toP(w.amount), concessionP - pinnedP);
    if (amountP <= 0) continue;
    const key = w.key ? lineKeyOfToken(w.key) : null;
    const exact = key === null ? -1 : specs.findIndex((l) => l.key === key && l.head === w.head);
    if (exact >= 0) {
      pinnedBy[exact] = (pinnedBy[exact] ?? 0) + amountP;
      pinnedP += amountP;
      continue;
    }
    // The row lacks that line: its category takes it - the category's own line, else the category's lines by billed weight.
    const ofHead = specs.flatMap((l, i) => (l.head === w.head ? [i] : []));
    if (ofHead.length === 0) continue; // a category this row does not touch: stays in the spread
    const own = ofHead.find((i) => !specs[i]!.custom);
    if (own !== undefined) {
      pinnedBy[own] = (pinnedBy[own] ?? 0) + amountP;
    } else {
      const shares = allocatePaise(amountP, ofHead.map((i) => billedBy[i] ?? 0));
      ofHead.forEach((i, k) => {
        pinnedBy[i] = (pinnedBy[i] ?? 0) + (shares[k] ?? 0);
      });
    }
    pinnedP += amountP;
  }
  const spreadP = allocatePaise(concessionP - pinnedP, weights);
  const concessionBy = spreadP.map((c, i) => c + (pinnedBy[i] ?? 0));
  // A concession can never exceed what its own line bills: move any excess to a line with room.
  const capAtBilled = (by: number[]): void => {
    for (let i = 0; i < n; i += 1) {
      let excess = (by[i] ?? 0) - (billedBy[i] ?? 0);
      if (excess <= 0) continue;
      by[i] = billedBy[i] ?? 0;
      for (let j = 0; j < n && excess > 0; j += 1) {
        const room = (billedBy[j] ?? 0) - (by[j] ?? 0);
        if (room <= 0) continue;
        const move = Math.min(room, excess);
        by[j] = (by[j] ?? 0) + move;
        excess -= move;
      }
    }
  };
  capAtBilled(concessionBy);
  const fineBy = allocatePaise(fineP, weights);
  const netBy = specs.map((_, i) => Math.max(0, (billedBy[i] ?? 0) - (concessionBy[i] ?? 0) + (fineBy[i] ?? 0)));

  // ── The "virtual pre-refund row" reading (CAMP-2026-0942) ────────────────────────────────────
  // A pinned waiver is a refunded line: its charge was cancelled AND its paid money was paid back. What is paid is attributed
  // (directed money first, the rest pro rata by remaining room) on the row as it stood BEFORE the refund - net plus the waivers,
  // paid plus the refunded amounts, the tagged refund events added back to the directed sums - and each refunded line then gives up
  // its refund. Refunding a line therefore leaves every other line's paid and due exactly where they were; re-spreading the reduced
  // paid over the reduced room would shift them. With nothing pinned every value below is the original one (no copy, no change).
  let netAttr = netBy;
  let rowPaidAttrP = rowPaidP;
  let directedAttr: readonly LineDirected[] = directed;
  if (pinnedP > 0) {
    const concessionV = [...spreadP];
    capAtBilled(concessionV);
    netAttr = specs.map((_, i) => Math.max(0, (billedBy[i] ?? 0) - (concessionV[i] ?? 0) + (fineBy[i] ?? 0)));
    rowPaidAttrP = rowPaidP + pinnedP;
    // Put each refund back on the directed sum of its line (the refund event is tagged with the line, so that group is net of it).
    const adjusted: LineDirected[] = [...directed];
    for (let i = 0; i < n; i += 1) {
      const r = pinnedBy[i] ?? 0;
      if (r <= 0) continue;
      const spec = specs[i]!;
      let sumP = 0;
      let tagged = false;
      for (let k = adjusted.length - 1; k >= 0; k -= 1) {
        const e = adjusted[k]!;
        if (e.head === spec.head && lineKeyOfToken(e.token) === spec.key) {
          sumP += rupeesToPaiseNumber(e.amount);
          tagged = true;
          adjusted.splice(k, 1);
        }
      }
      // Only a refund whose event carries the line has anything to add back; an untagged one never touched the directed sums.
      if (tagged) adjusted.push({ head: spec.head, token: spec.token, amount: fromP(sumP + r) });
    }
    directedAttr = adjusted;
  }

  // ── directed money: onto its own line, or spread over its category's lines ──
  const indexOfKey = new Map<string, number>(specs.map((l, i) => [l.key, i]));
  const directedBy = new Array<number>(n).fill(0);
  const categoryLevel = new Map<FeeHead, number>();
  for (const e of directedAttr) {
    const amountP = toP(e.amount);
    if (amountP <= 0) continue;
    const key = lineKeyOfToken(e.token);
    const at = key === null ? undefined : indexOfKey.get(key);
    if (at !== undefined && specs[at]!.head === e.head) {
      directedBy[at] = (directedBy[at] ?? 0) + amountP;
    } else {
      categoryLevel.set(e.head, (categoryLevel.get(e.head) ?? 0) + amountP);
    }
  }
  for (const [head, dP] of categoryLevel) {
    const idx = specs.flatMap((l, i) => (l.head === head ? [i] : []));
    if (idx.length === 0) continue; // a category this row doesn't touch: ignored, as the head engine does
    if (idx.length === 1) {
      directedBy[idx[0]!] = (directedBy[idx[0]!] ?? 0) + dP;
      continue;
    }
    const caps = idx.map((i) => Math.max(0, (netAttr[i] ?? 0) - (directedBy[i] ?? 0)));
    const capTotal = caps.reduce((s, c) => s + c, 0);
    if (capTotal <= 0) {
      const last = idx[idx.length - 1]!;
      directedBy[last] = (directedBy[last] ?? 0) + dP;
    } else if (dP >= capTotal) {
      idx.forEach((i, k) => {
        const extra = k === idx.length - 1 ? dP - capTotal : 0;
        directedBy[i] = (directedBy[i] ?? 0) + (caps[k] ?? 0) + extra;
      });
    } else {
      const shares = allocatePaise(dP, caps);
      idx.forEach((i, k) => {
        directedBy[i] = (directedBy[i] ?? 0) + (shares[k] ?? 0);
      });
    }
  }
  const directedTotalP = directedBy.reduce((s, d) => s + d, 0);

  const undirectedP = rowPaidAttrP - directedTotalP;
  let paidBy: number[];
  let extraAdvanceP = 0;
  if (undirectedP >= 0) {
    const caps = specs.map((_, i) => Math.max(0, (netAttr[i] ?? 0) - (directedBy[i] ?? 0)));
    const capTotal = caps.reduce((s, c) => s + c, 0);
    if (capTotal <= 0) {
      paidBy = [...directedBy];
      extraAdvanceP = undirectedP;
    } else if (undirectedP >= capTotal) {
      paidBy = specs.map((_, i) => (directedBy[i] ?? 0) + (caps[i] ?? 0));
      extraAdvanceP = undirectedP - capTotal;
    } else {
      const shares = allocatePaise(undirectedP, caps);
      paidBy = specs.map((_, i) => (directedBy[i] ?? 0) + (shares[i] ?? 0));
    }
  } else {
    // A correction pulled more cash off the row than was ever directed to a line:
    // shrink the directed lines pro rata so none is left showing money the row no longer holds.
    const cut = allocatePaise(Math.min(-undirectedP, directedTotalP), directedBy);
    paidBy = specs.map((_, i) => Math.max(0, (directedBy[i] ?? 0) - (cut[i] ?? 0)));
  }

  // Applied paid per line on the attribution row; what a line holds above its room is advance.
  let overflowP = 0;
  const appliedBy = specs.map((_, i) => {
    const paidRaw = paidBy[i] ?? 0;
    const applied = Math.min(paidRaw, netAttr[i] ?? 0);
    overflowP += paidRaw - applied;
    return applied;
  });
  let advanceP = extraAdvanceP + overflowP;
  const paidFinal = [...appliedBy];
  if (pinnedP > 0) {
    // Each refunded line gives up its refund. A refund larger than the line's attributed paid (a later correction took the money)
    // leaves a shortfall: it comes out of the advance first, then pro rata from the lines still holding money, so nothing goes
    // negative and Σ line paid + advance stays equal to what the row holds.
    let deficitP = 0;
    for (let i = 0; i < n; i += 1) {
      const r = pinnedBy[i] ?? 0;
      deficitP += Math.max(0, r - (appliedBy[i] ?? 0));
      paidFinal[i] = Math.max(0, (appliedBy[i] ?? 0) - r);
    }
    const fromAdvance = Math.min(deficitP, advanceP);
    advanceP -= fromAdvance;
    deficitP -= fromAdvance;
    if (deficitP > 0) {
      const cut = allocatePaise(Math.min(deficitP, paidFinal.reduce((sum, v) => sum + v, 0)), paidFinal);
      for (let i = 0; i < n; i += 1) paidFinal[i] = Math.max(0, (paidFinal[i] ?? 0) - (cut[i] ?? 0));
    }
  }
  const lines: LineHeadLine[] = specs.map((l, i) => {
    const net = netBy[i] ?? 0;
    const applied = Math.min(paidFinal[i] ?? 0, net);
    return {
      key: l.key,
      head: l.head,
      label: l.label,
      custom: l.custom,
      token: l.token,
      billed: fromP(billedBy[i] ?? 0),
      concession: fromP(concessionBy[i] ?? 0),
      ...((pinnedBy[i] ?? 0) > 0 ? { refunded: fromP(Math.min(pinnedBy[i] ?? 0, concessionBy[i] ?? 0)) } : {}),
      fine: fromP(fineBy[i] ?? 0),
      net: fromP(net),
      paid: fromP(applied),
      due: fromP(Math.max(0, net - applied)),
    };
  });

  return { lines, several: null, advance: fromP(advanceP) };
}

// ── Student-level aggregate ──────────────────────────────────────────────────

export interface LineBreakdownRowInput {
  id: string;
  money: RowMoney;
  composition: RowLineComposition;
  directed?: readonly LineDirected[];
  /** Waivers that belong to one line (a refunded fee line); see `PinnedWaiver`. */
  pinned?: readonly PinnedWaiver[];
}

export interface StudentLine extends LineHeadLine {
  /** True when no collection event ever named this line's category, so its `paid` split rests on pro-rata apportioning. */
  derived: boolean;
}

/**
 * Aggregate several rows' line breakdowns into the student's lines. Lines merge by
 * key across rows ("IIT Fee" on installments 1, 2 and 3 is one line); the first
 * row's spelling of a custom name is the label. Bundle ("Several heads") rows are
 * not lines — the head breakdown already reports them as `several`.
 */
export function computeStudentLines(rows: readonly LineBreakdownRowInput[]): StudentLine[] {
  type Acc = { line: LineHeadLine; onlyLine: boolean; billed: number; concession: number; refunded: number; fine: number; net: number; paid: number; due: number };
  const byKey = new Map<string, Acc>();
  const directedHeads = new Set<FeeHead>();
  for (const row of rows) {
    for (const e of row.directed ?? []) if (e.amount > 0) directedHeads.add(e.head);
    const b = computeRowLineBreakdown(row.money, row.composition, row.directed ?? [], row.pinned ?? []);
    for (const line of b.lines) {
      const seen = byKey.get(line.key);
      if (seen) {
        seen.onlyLine = seen.onlyLine && b.lines.length === 1;
        seen.billed += toP(line.billed);
        seen.concession += toP(line.concession);
        seen.refunded += toP(line.refunded ?? 0);
        seen.fine += toP(line.fine);
        seen.net += toP(line.net);
        seen.paid += toP(line.paid);
        seen.due += toP(line.due);
      } else {
        byKey.set(line.key, {
          line,
          onlyLine: b.lines.length === 1,
          billed: toP(line.billed),
          concession: toP(line.concession),
          refunded: toP(line.refunded ?? 0),
          fine: toP(line.fine),
          net: toP(line.net),
          paid: toP(line.paid),
          due: toP(line.due),
        });
      }
    }
  }
  return [...byKey.values()]
    .sort((a, b) => compareLines(a.line, b.line))
    .map(({ line, onlyLine, billed, concession, refunded, fine, net, paid, due }) => ({
      ...line,
      billed: fromP(billed),
      concession: fromP(concession),
      ...(refunded > 0 ? { refunded: fromP(refunded) } : {}),
      fine: fromP(fine),
      net: fromP(net),
      paid: fromP(paid),
      due: fromP(due),
      // Estimated only when the split rests on pro-rata apportioning: a line that is the ONLY line of every row it sits on holds
      // exactly what its row holds (min(paid, net)), whatever named the money.
      derived: !directedHeads.has(line.head) && !onlyLine,
    }));
}

/**
 * How much of ONE row a directed payment towards a LINE may reach. A bundle row of
 * unknown composition follows the head rule (`headDirectedCapacity`): TRANSPORT
 * never reaches it, any other head may settle it whole.
 */
export function lineDirectedCapacity(breakdown: RowLineBreakdown, head: FeeHead, token: string): number {
  if (breakdown.several) return head === "TRANSPORT" ? 0 : breakdown.several.due;
  const key = lineKeyOfToken(token);
  if (!key) return 0;
  return breakdown.lines.find((l) => l.key === key && l.head === head)?.due ?? 0;
}

// ── "Apply towards" options ──────────────────────────────────────────────────

export interface ApplyTowardsOption {
  /** Stable id for a React key / radio value: the line key, or the bare category for a placeholder. */
  id: string;
  head: FeeHead;
  /** What the chip says: the category label or the component's own name. */
  label: string;
  due: number;
  /**
   * What to send as `applyTo.component`. Undefined when the category alone names
   * the money unambiguously (a category with a single line, or a ₹0 placeholder):
   * the request is then exactly the one this screen has always sent.
   */
  component: string | undefined;
  /** True for a category with nothing billed under it (shown disabled, as before). */
  placeholder: boolean;
}

/**
 * CAMP-2026-1030 - the "Previous year due" choice under "Apply towards". A previous-year due is an
 * opening-balance installment with NO fee head (rule 19c), so it can never be one of the head chips above;
 * it is its own target, sent as `applyToPreviousDue: true`, and the server directs that money at the
 * pupil's open opening-balance rows only. `due` is what `GET .../head-breakdown` reports as
 * `previousDue.due` - the same figure the server enforces as the ceiling.
 */
export interface PreviousDueOption {
  id: typeof PREVIOUS_DUE_OPTION_ID;
  label: string;
  due: number;
}
export const PREVIOUS_DUE_OPTION_ID = "PREVIOUS_DUE" as const;
export const PREVIOUS_DUE_LABEL = "Previous year due";

/** The choice to offer, or null when the pupil has nothing open on a previous-year due (it is then not shown at all). */
export function previousDueOption(previousDue: { due: number } | null | undefined): PreviousDueOption | null {
  const due = previousDue?.due ?? 0;
  if (!Number.isFinite(due) || due <= 0) return null;
  return { id: PREVIOUS_DUE_OPTION_ID, label: PREVIOUS_DUE_LABEL, due };
}

/**
 * The chips for "Apply towards": every line the student owes, by its real name,
 * plus a ₹0 placeholder for each category with no line (the screen has always listed
 * all six so a head with nothing due reads as "₹0", not as missing).
 */
export function applyTowardsOptions(lines: readonly StudentLine[] | null | undefined): ApplyTowardsOption[] {
  const all = lines ?? [];
  const out: ApplyTowardsOption[] = [];
  for (const head of HEAD_ORDER) {
    const ofHead = all.filter((l) => l.head === head);
    if (ofHead.length === 0) {
      out.push({ id: head, head, label: FEE_HEAD_LABELS[head], due: 0, component: undefined, placeholder: true });
      continue;
    }
    const needsToken = ofHead.length > 1;
    for (const l of ofHead) {
      out.push({
        id: l.key,
        head,
        label: l.label,
        due: l.due,
        component: needsToken || l.custom ? l.token : undefined,
        placeholder: false,
      });
    }
  }
  return out;
}
